'use client'

import { App, Button, Form, Input, Result, Spin } from 'antd'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { type ReactNode, useEffect, useEffectEvent, useState } from 'react'
import { API_ERROR, type ApiError, CHALLENGE } from './contract'
import { GoogleLoginButton } from './google.login.button'
import { isRecord } from './is.record'
import type { LoginApi } from './login.api'
import type { GoogleLoginTexts, LoginFormTexts } from './texts'

/** How the user signed in, as reported to {@link LoginFormProps.onLoggedIn}. */
export type LoginMethod = 'password' | 'google'

/** Steps of the flow the app may want to record, e.g. as analytics events. */
export type LoginEvent = 'identify' | 'email-sent'

/** Props of {@link LoginForm}. */
export interface LoginFormProps {
  api: LoginApi
  texts: LoginFormTexts
  /** URL path of the page the emailed login link opens; `?token=` is appended to it. */
  pendingPath: string
  /** URL path of the page that replaces an expired password; `?token=` is appended to it. */
  changePasswordPath: string
  /** Called once the session cookie is set; the app navigates on. */
  onLoggedIn: (method: LoginMethod) => void
  /** Called when the form opens (`identify`) and when a login link was emailed (`email-sent`). */
  onEvent?: (event: LoginEvent) => void
  /** Reports unexpected failures, e.g. to error tracking. */
  onError?: (error: unknown) => void
  /** Offers "Sign in with Google" below the form. */
  google?: { clientId: string; texts: GoogleLoginTexts }
  /**
   * Follows the API's `CHALLENGE_ZITADEL` into next-auth's Zitadel sign-in, which returns to
   * `redirectTo` (default: the current page). Without it, that challenge counts as unsupported.
   */
  zitadel?: { redirectTo?: string }
  /** Disables the form, e.g. when the browser cannot store what the app needs. */
  disabled?: boolean
  /** Shown below the email field, e.g. terms of service. */
  notice?: ReactNode
  /** Shown below the email step's button, e.g. a link to registration. */
  footer?: ReactNode
}

interface LoginValues {
  email: string
  password: string
}

type Step = 'email' | 'password' | 'unsupported'

const hasError = (errors: ApiError[], code: string) => errors.some((e) => e.message === code)

/** The change-password token of a `PASSWORD_EXPIRED` error, if it carries one. */
function expiredPasswordToken(errors: ApiError[]): string | undefined {
  const error = errors.find((e) => e.message === API_ERROR.passwordExpired)
  const original = error?.extensions?.originalError
  return isRecord(original) && typeof original.error === 'string' ? original.error : undefined
}

/**
 * Email-first sign-in of the Tucan apps. The user enters an email address and the API decides
 * how they sign in: with their password (second step), with a login link it emails
 * (`pendingPath` opens), or through Zitadel. Google sign-in is offered alongside when configured.
 *
 * Renders inside a {@link CenterCard}; needs antd's `<App>` for its toasts.
 */
export function LoginForm({
  api,
  texts,
  pendingPath,
  changePasswordPath,
  onLoggedIn,
  onEvent,
  onError,
  google,
  zitadel,
  disabled,
  notice,
  footer,
}: LoginFormProps) {
  const router = useRouter()
  const { message } = App.useApp()
  const [form] = Form.useForm<LoginValues>()
  const [step, setStep] = useState<Step>('email')
  const [submitting, setSubmitting] = useState(false)
  /** Address of the last challenge; the password step signs in with it. */
  const [email, setEmail] = useState('')

  const identify = useEffectEvent(() => onEvent?.('identify'))
  useEffect(() => identify(), [])

  const requestChallenge = async (address: string, verifyWithPassword: boolean) => {
    setEmail(address)
    setSubmitting(true)
    const result = await api.requestChallenge({
      email: address,
      redirect: `${window.location.origin}${pendingPath}?token=`,
      verifyWithPassword,
    })

    if (!result.ok) {
      setSubmitting(false)
      if (hasError(result.errors, API_ERROR.email)) {
        void message.error(texts.userNotFound)
      } else if (hasError(result.errors, API_ERROR.redirect)) {
        void message.error(texts.invalidRedirect)
      } else {
        onError?.(result.errors)
        void message.error(texts.requestFailed)
      }
      return
    }

    switch (result.status) {
      case CHALLENGE.email:
        void message.success(texts.emailSent)
        onEvent?.('email-sent')
        router.push(pendingPath)
        return
      case CHALLENGE.password:
        setSubmitting(false)
        setStep('password')
        return
      case CHALLENGE.zitadel:
        if (zitadel) {
          void signIn('zitadel', zitadel.redirectTo ? { redirectTo: zitadel.redirectTo } : {}, {
            login_hint: address,
            prompt: 'login',
          })
          return
        }
    }
    setSubmitting(false)
    setStep('unsupported')
  }

  const loginWithPassword = async (password: string) => {
    setSubmitting(true)
    const result = await api.loginWithPassword({ email, password })

    if (result.ok) {
      void message.success(texts.loggedIn)
      onLoggedIn('password')
      return
    }

    setSubmitting(false)
    const token = expiredPasswordToken(result.errors)
    if (token) {
      void message.error(texts.passwordExpired)
      router.push(`${changePasswordPath}?token=${encodeURIComponent(token)}`)
    } else {
      void message.error(texts.passwordLoginFailed)
    }
  }

  if (step === 'unsupported') {
    return (
      <Result
        status="info"
        title={texts.unsupportedChallenge}
        extra={<Button onClick={() => setStep('email')}>{texts.backToLogin}</Button>}
      />
    )
  }

  if (step === 'password') {
    return (
      <>
        <h1>{texts.passwordTitle}</h1>
        <Spin spinning={submitting}>
          <Form<LoginValues>
            form={form}
            layout="vertical"
            onFinish={(values) => void loginWithPassword(values.password)}
          >
            <Form.Item name="email" label={texts.emailLabel}>
              <Input disabled size="large" />
            </Form.Item>
            <Form.Item
              name="password"
              label={texts.passwordLabel}
              rules={[{ message: texts.passwordRequired, required: true }]}
            >
              <Input.Password autoComplete="current-password" autoFocus size="large" />
            </Form.Item>
            <Form.Item>
              <Button disabled={disabled} size="large" type="primary" htmlType="submit" block>
                {texts.passwordSubmit}
              </Button>
              <Button
                size="large"
                type="link"
                block
                onClick={() => void requestChallenge(email, false)}
              >
                {texts.sendEmailLinkInstead}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </>
    )
  }

  return (
    <>
      <h1>{texts.title}</h1>
      <Spin spinning={submitting}>
        <Form<LoginValues>
          form={form}
          layout="vertical"
          onFinish={(values) => void requestChallenge(values.email, true)}
        >
          <Form.Item
            name="email"
            label={texts.emailLabel}
            rules={[
              { message: texts.emailRequired, required: true },
              { message: texts.emailInvalid, type: 'email' },
            ]}
          >
            <Input
              autoComplete="email"
              autoFocus
              disabled={disabled}
              placeholder={texts.emailPlaceholder}
              size="large"
            />
          </Form.Item>
          {notice}
          <Form.Item>
            <Button disabled={disabled} size="large" type="primary" htmlType="submit" block>
              {texts.submit}
            </Button>
            {footer}
          </Form.Item>
          {google && (
            <Form.Item>
              <GoogleLoginButton
                clientId={google.clientId}
                api={api}
                texts={google.texts}
                disabled={disabled}
                onError={onError}
                onSubmitting={setSubmitting}
                onSuccess={() => onLoggedIn('google')}
              />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </>
  )
}
